/* Signal Hunting · Stap 1
 *
 * Sales Hunter agent vindt continu groei-signalen bij target-bedrijven.
 * Elk signaal heeft minstens 1 contactpersoon (agents praten met mensen).
 *
 *   • Date filter (Vandaag / 7d / 30d / Custom)
 *   • Type filter (funding/hiring/expansion/leadership/launch)
 *   • Hunt Missions: gebruikers geven Sales Hunter een opdracht ("vind alle
 *     HR-directeurs van …") en kiezen schedule + datasource.
 *   • Inline contacts per signal — toevoegen/verwijderen.
 *   • Promote → Company kopieert de eerste contactpersoon naar de Lead +
 *     andere contacten worden stakeholders.
 *
 * Refactor: subcomponenten leven in ./signals/ —
 *   SmartHuntsRow · HuntMissionsRow · SignalCard · MissionCreateModal ·
 *   PromoteCampaignPicker
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "../Dashboard";
import LayoffRadarButton from "@/components/sales/LayoffRadarButton";
import { toast } from "sonner";
import {
    Radar, X, Loader2, Target, Trash2, Calendar,
    Archive, Eye, EyeOff, ThumbsUp, ThumbsDown, ShieldCheck, AlertTriangle, Layers,
} from "lucide-react";
import RejectReasonModal from "@/components/common/RejectReasonModal";
import { SmartHuntsRow } from "./signals/SmartHuntsRow";
import { HuntMissionsRow } from "./signals/HuntMissionsRow";
import { SignalCard } from "./signals/SignalCard";
import { MissionCreateModal } from "./signals/MissionCreateModal";
import { PromoteCampaignPicker } from "./signals/PromoteCampaignPicker";

const DATE_PRESETS = [
    { id: "all", get label() { return i18n.t("common.all"); } },
    { id: "today", get label() { return i18n.t("signals.date_today"); } },
    { id: "7d",  get label() { return i18n.t("signals.date_7d"); } },
    { id: "30d", get label() { return i18n.t("signals.date_30d"); } },
    { id: "custom", label: "Custom" },
];

function dateRange(preset, custom) {
    const now = new Date();
    if (preset === "all") return {};
    if (preset === "today") {
        const d = new Date(now); d.setHours(0, 0, 0, 0);
        return { from: d.toISOString() };
    }
    if (preset === "7d") {
        const d = new Date(now); d.setDate(d.getDate() - 7);
        return { from: d.toISOString() };
    }
    if (preset === "30d") {
        const d = new Date(now); d.setDate(d.getDate() - 30);
        return { from: d.toISOString() };
    }
    if (preset === "custom") {
        const r = {};
        if (custom?.from) r.from = new Date(custom.from + "T00:00:00").toISOString();
        if (custom?.to)   r.to   = new Date(custom.to   + "T23:59:59").toISOString();
        return r;
    }
    return {};
}

export default function SalesSignals() {
    const { t } = useTranslation();
    const { workspace } = useAuth();
    const [signals, setSignals] = useState([]);
    const [missions, setMissions] = useState([]);
    const [typeFilter, setTypeFilter] = useState([]);   // multiselect — empty = all
    // Trust-filter (anti-hallucinatie): "all" | "70" | "50" | "40" | "suspect" | "unverified"
    const [trustFilter, setTrustFilter] = useState("all");
    const [datePreset, setDatePreset] = useState("all");
    const [customDates, setCustomDates] = useState({ from: "", to: "" });
    const [loading, setLoading] = useState(true);
    const [missionId, setMissionId] = useState("");
    const [showMissionModal, setShowMissionModal] = useState(false);
    const [promoting, setPromoting] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkPromoting, setBulkPromoting] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [showSynthetic, setShowSynthetic] = useState(false);
    // Reject modal state — captures a reason + optional category then archives
    // the signal with agent_memory training. Also used from bulk-action bar.
    const [pendingReject, setPendingReject] = useState(null); // { signal } | { bulk: true, signals: [] }

    const load = async () => {
        const range = dateRange(datePreset, customDates);
        const params = new URLSearchParams();
        if (typeFilter && typeFilter.length > 0) params.set("signal_type", typeFilter.join(","));
        if (range.from) params.set("from_date", range.from);
        if (range.to)   params.set("to_date", range.to);
        if (missionId)  params.set("hunt_mission_id", missionId);
        if (showArchived) params.set("include_archived", "true");
        if (showSynthetic) params.set("include_synthetic", "true");
        const [a, b] = await Promise.all([
            api.get(`/signals?${params.toString()}`),
            api.get("/hunt-missions"),
        ]);
        setSignals(a.data || []);
        setMissions(b.data || []);
        setLoading(false);
    };
    useEffect(() => { load(); }, [typeFilter, datePreset, customDates.from, customDates.to, missionId, showArchived, showSynthetic]);

    // Anti-hallucinatie beleid (2b): automatisch verifiëren vóór promote.
    // Alleen waarschuwen bij lage trust — geen harde blokkade.
    const ensureVerified = async (sig) => {
        if (!sig) return true;
        let ver = sig.verification;
        if (!ver) {
            toast.info(t("verify.auto_running"));
            try { ver = (await api.post(`/verify/signal/${sig.id}`)).data; }
            catch { ver = null; }
        }
        if (ver && ver.trust_score < 50) {
            return confirm(t("verify.low_trust_confirm", { score: ver.trust_score }));
        }
        return true;
    };

    const promote = async (id) => {
        const sig = signals.find((s) => s.id === id);
        if (!(await ensureVerified(sig))) { load(); return; }
        setPromoting(id);
        try {
            await api.post(`/signals/${id}/promote`);
            toast.success(i18n.t("signals.promoted_ok"));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("signals.promote_failed"));
        } finally { setPromoting(null); }
    };

    // Promote a signal AND enroll the resulting lead into the given campaign
    // in one atomic UX action (2 API calls chained). Returns the lead id on
    // success so the caller can navigate or bulk-continue.
    const promoteAndEnroll = async (signalId, campaignId) => {
        const sig = signals.find((s) => s.id === signalId);
        if (!(await ensureVerified(sig))) { load(); return; }
        setPromoting(signalId);
        try {
            const promoteRes = await api.post(`/signals/${signalId}/promote`);
            const leadId = promoteRes.data?.id;
            if (!leadId) throw new Error(i18n.t("signals.promote_no_id"));
            const enrolRes = await api.post(`/campaigns/${campaignId}/enroll`, { lead_ids: [leadId] });
            toast.success(i18n.t("signals.promoted_enrolled", { count: enrolRes.data?.added || 1 }));
            load();
            return leadId;
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("signals.promote_enroll_failed"));
        } finally { setPromoting(null); }
    };

    const archiveSignal = async (id) => {
        try {
            await api.post(`/signals/${id}/archive`);
            toast.success(i18n.t("signals.archived_ok"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.archive_failed")); }
    };

    // Reject = archive + save reason to agent_memory. Opens the shared modal
    // so the agent learns from the rejection. The reason is mandatory.
    const openRejectModal = (signal) => setPendingReject({ signal });
    const confirmReject = async (reason, category, remember) => {
        if (!pendingReject) return;
        // Bulk variant
        if (pendingReject.bulk && Array.isArray(pendingReject.signals)) {
            try {
                // Persist a single workspace-level memory line (bulk share the same reason)
                if (remember && reason) {
                    await api.post("/memory", { reason, category, entity_type: "workspace" });
                }
                // Then archive them in one shot
                await api.post("/signals/bulk-archive", { signal_ids: pendingReject.signals });
                toast.success(i18n.t("signals.bulk_rejected", { count: pendingReject.signals.length }));
                setSelected(new Set());
                load();
            } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.bulk_reject_failed")); }
            return;
        }
        // Single signal reject
        try {
            await api.post(`/signals/${pendingReject.signal.id}/reject`, {
                reason, category, remember,
            });
            toast.success(i18n.t("signals.rejected_ok"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.reject_failed")); }
    };
    const restoreSignal = async (id) => {
        try {
            await api.post(`/signals/${id}/restore`);
            toast.success(i18n.t("signals.restored_ok"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.restore_failed")); }
    };
    const deleteSignal = async (id, name) => {
        if (!confirm(i18n.t("signals.delete_confirm", { name: name || "?" }))) return;
        try {
            await api.delete(`/signals/${id}`);
            toast.success(i18n.t("signals.deleted_ok"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.delete_failed")); }
    };

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectVisibleEligible = () => {
        // Selecteer álle zichtbare signalen (alle tikboxen aan) zodat bulk-acties
        // zoals verwijderen in één keer kunnen. Veiligheid rond lage trust-scores
        // wordt bij bulk-promote zelf afgehandeld (waarschuwing aldaar).
        setSelected(new Set(visibleSignals.map((s) => s.id)));
    };
    const clearSelection = () => setSelected(new Set());
    const bulkPromote = async () => {
        if (selected.size === 0) return;
        // Anti-hallucinatie: tel niet-geverifieerde / lage-trust signalen in de selectie
        const risky = signals.filter((s) => selected.has(s.id) && (!s.verification || s.verification.trust_score < 50)).length;
        const warn = risky > 0 ? `\n\n${t("verify.bulk_unverified", { n: risky })}` : "";
        if (!confirm(`${selected.size} signalen tegelijk promoten tot Companies?${warn}`)) return;
        setBulkPromoting(true);
        try {
            const r = await api.post("/signals/bulk-promote", { signal_ids: Array.from(selected) });
            toast.success(r.data.summary);
            setSelected(new Set());
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("signals.bulk_promote_failed"));
        } finally { setBulkPromoting(false); }
    };
    // Bulk-variant of promoteAndEnroll: promotes selection, then enrolls the
    // resulting lead ids (only newly promoted ones, no idempotency worries).
    const bulkPromoteAndEnroll = async (campaignId) => {
        if (selected.size === 0) return;
        if (!confirm(i18n.t("signals.bulk_promote_enroll_confirm", { count: selected.size }))) return;
        setBulkPromoting(true);
        try {
            const r = await api.post("/signals/bulk-promote", { signal_ids: Array.from(selected) });
            const leadIds = r.data?.promoted || [];  // backend returns list of lead_id strings
            const enrol = leadIds.length
                ? await api.post(`/campaigns/${campaignId}/enroll`, { lead_ids: leadIds })
                : { data: { added: 0 } };
            toast.success(i18n.t("signals.bulk_promoted", { promoted: leadIds.length, enrolled: enrol.data.added }));
            setSelected(new Set());
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("signals.bulk_promote_enroll_failed"));
        } finally { setBulkPromoting(false); }
    };
    const bulkArchive = async () => {
        if (selected.size === 0) return;
        if (!confirm(i18n.t("signals.bulk_archive_confirm", { count: selected.size }))) return;
        setBulkBusy(true);
        try {
            // Backend limiteert op 100 per batch → chunk zodat "alles selecteren" ook bij grote aantallen werkt.
            const ids = Array.from(selected);
            let last = null;
            for (let i = 0; i < ids.length; i += 100) {
                last = await api.post("/signals/bulk-archive", { signal_ids: ids.slice(i, i + 100) });
            }
            toast.success(last.data.summary);
            setSelected(new Set()); load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.bulk_archive_failed")); }
        finally { setBulkBusy(false); }
    };
    const bulkDelete = async () => {
        if (selected.size === 0) return;
        if (!confirm(i18n.t("signals.bulk_delete_confirm", { count: selected.size }))) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selected);
            let deleted = 0;
            for (let i = 0; i < ids.length; i += 100) {
                const r = await api.post("/signals/bulk-delete", { signal_ids: ids.slice(i, i + 100) });
                deleted += r.data.deleted ?? 0;
            }
            toast.success(i18n.t("signals.bulk_deleted_n", { count: deleted || ids.length }));
            setSelected(new Set()); load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("signals.bulk_delete_failed")); }
        finally { setBulkBusy(false); }
    };

    // Client-side trust-filter over de (server-gefilterde) signalenlijst
    const visibleSignals = useMemo(() => signals.filter((s) => {
        if (trustFilter === "all") return true;
        const ver = s.verification;
        if (trustFilter === "unverified") return !ver;
        if (trustFilter === "suspect") return !!ver && ver.trust_score < 40;
        return !!ver && ver.trust_score >= parseInt(trustFilter, 10);
    }), [signals, trustFilter]);

    const types = [...new Set(signals.map((s) => s.signal_type))];
    const totalSignals = visibleSignals.length;
    const fundingCount = visibleSignals.filter((s) => s.signal_type === "funding").length;
    const hiringCount = visibleSignals.filter((s) => s.signal_type === "hiring").length;
    const avgScore = visibleSignals.length ? Math.round(visibleSignals.reduce((a, b) => a + (b.score || 0), 0) / visibleSignals.length) : 0;

    // "Genereer lichting": top-signalen → prospects → AI-drafts pending approval.
    const [batchBusy, setBatchBusy] = useState(false);
    const generateBatch = async () => {
        setBatchBusy(true);
        try {
            const r = await api.post("/draft-batch/run", { pillar: "sales" });
            toast.success(i18n.t("batch.started", { count: r.data.limit }), {
                description: i18n.t("batch.started_desc"),
            });
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("batch.failed")); }
        finally { setBatchBusy(false); }
    };

    return (
        <PageHeader
            title={t("signals.title")}
            subtitle={t("signals.subtitle")}
            actions={
                <div className="flex items-center gap-2">
                    <LayoffRadarButton onDone={load} />
                    <button onClick={generateBatch} disabled={batchBusy} data-testid="signals-batch-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--jr-brand)]/40 text-[var(--jr-brand)] text-sm font-medium hover:bg-[var(--jr-brand)]/10 disabled:opacity-50">
                        {batchBusy ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />}
                        {t("batch.run")}
                    </button>
                    <button onClick={() => setShowMissionModal(true)} data-testid="hunt-mission-create-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                        <Target size={14} /> {t("signals.new_mission")}
                    </button>
                </div>
            }
        >
            {/* Slimme hunts (Sprint 1: look-alike / buyer-intent / competitor-churn) */}
            <SmartHuntsRow onRun={load} />

            {/* Hunt Missions row */}
            <HuntMissionsRow missions={missions} activeId={missionId} onPick={setMissionId} onChanged={load} />

            {/* Stats hero */}
            <div className="my-4 rounded-xl border border-[var(--jr-brand)]/30 bg-gradient-to-br from-[var(--jr-brand)]/10 to-transparent p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Radar className="text-[var(--jr-brand)]" size={20} />
                        <div>
                            <div className="font-display text-xl tracking-tight">{totalSignals} {t("signals.active_signals")}</div>
                            <div className="text-xs text-[var(--jr-text-2)]">{t("signals.monitors")}</div>
                        </div>
                    </div>
                    <div className="flex gap-6 text-xs">
                        <Mini label="Funding" v={fundingCount} />
                        <Mini label="Hiring" v={hiringCount} />
                        <Mini label="Gem. score" v={avgScore} />
                    </div>
                </div>
            </div>

            {/* Date + type filters */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <DateFilter preset={datePreset} setPreset={setDatePreset} custom={customDates} setCustom={setCustomDates} />
                <div className="flex gap-1 flex-wrap">
                    <Pill active={typeFilter.length === 0} onClick={() => setTypeFilter([])} testid="signals-filter-all">{t("signals.all_types")}</Pill>
                    {types.map((tp) => {
                        const active = typeFilter.includes(tp);
                        const toggle = () => setTypeFilter((prev) => active ? prev.filter((x) => x !== tp) : [...prev, tp]);
                        return <Pill key={tp} active={active} onClick={toggle} testid={`signals-filter-${tp}`}>{tp}</Pill>;
                    })}
                </div>
                <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived"
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${showArchived ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/30" : "text-[var(--jr-text-3)] border-white/10 hover:text-white hover:bg-white/5"}`}>
                    {showArchived ? <Eye size={12} /> : <EyeOff size={12} />}
                    {showArchived ? t("signals.show_archived") : t("signals.hide_archived")}
                </button>
                <button onClick={() => setShowSynthetic((v) => !v)} data-testid="toggle-synthetic"
                    title={t("signals.synthetic_toggle_tip")}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${showSynthetic ? "bg-[var(--jr-error)]/15 text-[var(--jr-error)] border-[var(--jr-error)]/30" : "text-[var(--jr-text-3)] border-white/10 hover:text-white hover:bg-white/5"}`}>
                    <AlertTriangle size={12} />
                    {showSynthetic ? t("signals.show_synthetic") : t("signals.hide_synthetic")}
                </button>
                {/* Trust-filter — werk standaard alleen met betrouwbare signalen */}
                <div className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={12} className={trustFilter !== "all" ? "text-[#22c55e]" : "text-[var(--jr-text-3)]"}/>
                    <select value={trustFilter} onChange={(e) => setTrustFilter(e.target.value)}
                        data-testid="trust-filter"
                        title={t("verify.filter_label")}
                        className={`border rounded-full px-3 py-1.5 text-xs outline-none cursor-pointer transition-colors ${trustFilter !== "all" ? "bg-[#22c55e]/10 border-[#22c55e]/40 text-[#22c55e]" : "bg-[var(--jr-surface)] border-white/10 text-[var(--jr-text-2)]"}`}>
                        <option value="all">{t("verify.filter_all")}</option>
                        <option value="70">{t("verify.filter_70")}</option>
                        <option value="50">{t("verify.filter_50")}</option>
                        <option value="40">{t("verify.filter_40")}</option>
                        <option value="suspect">{t("verify.filter_suspect")}</option>
                        <option value="unverified">{t("verify.filter_unverified")}</option>
                    </select>
                </div>
                {missionId && (
                    <button onClick={() => setMissionId("")} data-testid="clear-mission-filter"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30">
                        Mission filter actief <X size={11} />
                    </button>
                )}
            </div>

            {loading ? <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div> : visibleSignals.length === 0 ? (
                <EmptyState onCreate={() => setShowMissionModal(true)} />
            ) : (
                <>
                    {/* Bulk-action bar */}
                    {selected.size > 0 ? (
                        <div data-testid="bulk-bar"
                            className="sticky top-0 z-10 mb-3 rounded-lg border border-[var(--jr-brand)]/40 bg-[var(--jr-brand)]/[0.10] backdrop-blur px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                            <span className="text-xs text-white">
                                <span className="font-mono text-[var(--jr-brand)]">{selected.size}</span> {selected.size === 1 ? i18n.t("signals.signal_word") : i18n.t("signals.signals_word")} {i18n.t("signals.selected_word")}
                            </span>
                            <div className="flex items-center gap-2 flex-wrap">
                                <button onClick={clearSelection} data-testid="bulk-clear"
                                    className="text-[10px] font-mono uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                                <button onClick={() => setPendingReject({ bulk: true, signals: Array.from(selected) })}
                                    disabled={bulkBusy} data-testid="bulk-reject"
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/20 disabled:opacity-50">
                                    <ThumbsDown size={12} /> {i18n.t("signals.reject_with_reason")}
                                </button>
                                <button onClick={bulkArchive} disabled={bulkBusy} data-testid="bulk-archive"
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white disabled:opacity-50">
                                    <Archive size={12} /> {i18n.t("signals.archive_silent")}
                                </button>
                                <button onClick={bulkDelete} disabled={bulkBusy} data-testid="bulk-delete"
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/20 disabled:opacity-50">
                                    <Trash2 size={12} /> {i18n.t("common.delete")}
                                </button>
                                <button onClick={bulkPromote} disabled={bulkPromoting || bulkBusy} data-testid="bulk-promote"
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-l-full bg-[#22c55e] text-black font-medium disabled:opacity-50 hover:bg-[#16a34a]">
                                    {bulkPromoting ? <Loader2 className="animate-spin" size={12} /> : <ThumbsUp size={12} />}
                                    {i18n.t("common.approve")} {selected.size}
                                </button>
                                <PromoteCampaignPicker signalId="bulk" onSelect={(_sid, cid) => bulkPromoteAndEnroll(cid)}
                                    disabled={bulkPromoting || bulkBusy} testidPrefix="bulk-atc" />
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-end mb-2">
                            <button onClick={selectVisibleEligible} data-testid="select-all"
                                className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white">
                                {i18n.t("signals.select_all")}
                            </button>
                        </div>
                    )}
                    <div className="space-y-3" data-testid="signals-list">
                        {visibleSignals.map((s) => (
                            <SignalCard key={s.id} signal={s} onPromote={promote} promoting={promoting === s.id}
                                onPromoteAndEnroll={promoteAndEnroll}
                                onChanged={load} missions={missions}
                                selected={selected.has(s.id)} onToggleSelect={() => toggleSelect(s.id)}
                                onArchive={archiveSignal} onRestore={restoreSignal} onDelete={deleteSignal}
                                onReject={openRejectModal} />
                        ))}
                    </div>
                </>
            )}

            {showMissionModal && (
                <MissionCreateModal
                    workspace={workspace}
                    onClose={() => setShowMissionModal(false)}
                    onCreated={async (mission) => {
                        setShowMissionModal(false);
                        toast.success(i18n.t("signals.mission_started", { title: mission.title }));
                        await load();
                    }}
                />
            )}
            {/* Reject-with-reason modal (single + bulk) */}
            <RejectReasonModal
                open={!!pendingReject}
                onClose={() => setPendingReject(null)}
                title={pendingReject?.bulk ? i18n.t("signals.bulk_reject_title", { count: pendingReject.signals?.length || 0 }) : i18n.t("signals.reject_title")}
                subtitle={pendingReject?.signal ? `${pendingReject.signal.company_name} — ${pendingReject.signal.signal_title}` : (pendingReject?.bulk ? i18n.t("signals.bulk_reject_sub") : "")}
                originalSnippet={pendingReject?.signal ? `${pendingReject.signal.signal_title}\n${pendingReject.signal.signal_detail || ""}` : ""}
                onConfirm={confirmReject}
                confirmLabel={i18n.t("signals.reject_confirm_label")}
            />
        </PageHeader>
    );
}

/* ------------------------------------------------------------------ */
/*  Date filter                                                        */
/* ------------------------------------------------------------------ */
function DateFilter({ preset, setPreset, custom, setCustom }) {
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1">
                <Calendar size={11} /> Datum
            </span>
            <div className="inline-flex rounded-full border border-white/10 bg-[var(--jr-surface)] overflow-hidden">
                {DATE_PRESETS.map((p) => (
                    <button key={p.id} onClick={() => setPreset(p.id)} data-testid={`date-${p.id}`}
                        className={`text-[11px] font-mono uppercase tracking-[0.14em] px-3 py-1.5 transition-colors ${preset === p.id ? "bg-[var(--jr-brand)] text-black" : "text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                        {p.label}
                    </button>
                ))}
            </div>
            {preset === "custom" && (
                <div className="flex items-center gap-2">
                    <input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="date-from"
                        className="bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none" />
                    <span className="text-[var(--jr-text-3)] text-xs">—</span>
                    <input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="date-to"
                        className="bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none" />
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Empty state + misc                                                 */
/* ------------------------------------------------------------------ */
function EmptyState({ onCreate }) {
    return (
        <div className="rounded-xl border border-dashed border-white/15 bg-[var(--jr-surface)] p-10 text-center">
            <Radar className="text-[var(--jr-brand)] mx-auto" size={28} />
            <p className="mt-4 text-[var(--jr-text-2)]">
                Geen signalen voor deze filter. Geef Sales Hunter een nieuwe opdracht of pas je datumfilter aan.
            </p>
            <button onClick={onCreate} data-testid="empty-mission-btn"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                <Target size={14} /> Start een hunt mission
            </button>
        </div>
    );
}

function Pill({ active, onClick, children, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`text-xs font-mono uppercase tracking-[0.16em] px-3 py-1.5 rounded-full transition-colors ${active ? "bg-[var(--jr-brand)] text-black" : "border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>{children}</button>
    );
}

function Mini({ label, v }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="font-display text-xl tracking-tight">{v}</div>
        </div>
    );
}
